import { useCallback, useEffect, useMemo, useState } from 'react';

import { FiPlus } from 'react-icons/fi';
import { MdFilterList } from 'react-icons/md';
import { useHistory, useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';

import { ButtonBlack } from '~/components';
import { useDataSheets } from '~/hooks/dataSheets';

import { DataSheetCard } from './DataSheetCard';
import Modal from './Modal';

import {
  AlertContainer,
  ButtonWrapper,
  CardContainer,
  FilterLabel,
  FilterOptions,
  ModalFilterContent,
  ModalQuestion,
  ModalTitle,
  NewButton,
  NewButtonContainer,
  NewButtonLabel,
  Options,
  RadioContainer,
  RadioInput,
  Wrapper,
  NoDataFound,
} from './styles';

type RouteParams = {
  userId: string;
};

type DataSheet = {
  id: number;
  student_id: number;
  professional_id: number;
  gym_id?: string;
  genre?: string;
  classification: string;
  objective: string;
  student_level: string;
  frequency: string;
  available_time: string;
  training_division?: string;
  week_days: string;
  health_history: string;
  injury_history: string;
  height: number;
  weight: number;
  goal_weight: number;
  body_fat: number;
  imc: number;
  birth_date: string;
  circumference_chest: number;
  circumference_waist: number;
  circumference_abdomen: number;
  circumference_hip: number;
  skin_folds_total: number;
  created_at: string;
  updated_at: string;
  time_preference: string;
  circumference_relaxed_arm_right: number;
  circumference_relaxed_arm_left: number;
  circumference_contracted_arm_right: number;
  circumference_contracted_arm_left: number;
  circumference_forearm_right: number;
  circumference_forearm_left: number;
  circumference_fist_right: number;
  circumference_fist_left: number;
  circumference_proximal_thigh_right: number;
  circumference_proximal_thigh_left: number;
  circumference_distal_thigh_right: number;
  circumference_distal_thigh_left: number;
  circumference_calf_right: number;
  circumference_calf_left: number;
  skin_folds_biceps_right: number;
  skin_folds_biceps_left: number;
  skin_folds_triceps_right: number;
  skin_folds_triceps_left: number;
  skin_folds_subscapular_right: number;
  skin_folds_subscapular_left: number;
  skin_folds_middle_armpit_right: number;
  skin_folds_middle_armpit_left: number;
  skin_folds_heigsupra_iliacaht_right: number;
  skin_folds_heigsupra_iliacaht_left: number;
  skin_folds_abdomen_right: number;
  skin_folds_abdomen_left: number;
  skin_folds_breastplate_right: number;
  skin_folds_breastplate_left: number;
  skin_folds_thigh_right: number;
  skin_folds_thigh_left: number;
  skin_folds_calf_right: number;
  skin_folds_calf_left: number;
};

export function DataSheet() {
  const { push } = useHistory();
  const { userId: studentId } = useParams<RouteParams>();
  const { dataSheetsList, loadDataSheets } = useDataSheets();

  const [loading, setLoading] = useState(true);
  const [technicalSheetModal, setTechnicalSheetModal] = useState(false);
  const [technicalSheetOrderModal, setTechnicalSheetOrderModal] =
    useState(false);
  const [radioOrderOptions, setRadioOrderOptions] = useState('1');

  // Pega fichas técnicas da API
  useEffect(() => {
    loadDataSheets(studentId);

    setLoading(false);
  }, [loadDataSheets, studentId]);

  function TechnicalSheetModal() {
    return (
      <Modal
        isOpen={technicalSheetModal}
        setOpen={setTechnicalSheetModal}
        maxWidthStyled={621}
        maxHeightStyled={428}
      >
        <ModalFilterContent>
          <ModalTitle>Filtrar</ModalTitle>

          <ModalQuestion>
            <h2>Data de Cadastro:</h2>

            <div className="date_register">
              <input type="number" placeholder="Dia" />
              <input type="number" placeholder="Mês" />
              <input type="number" placeholder="Ano" />
            </div>
          </ModalQuestion>

          <ButtonBlack>Aplicar</ButtonBlack>
        </ModalFilterContent>
      </Modal>
    );
  }

  const radioOrderOptionsList = useMemo(
    () => [
      { value: '1', label: 'Por id de criação' },
      { value: '2', label: 'Por data de criação' },
    ],
    [],
  );

  const RadioOrderOptions = useMemo(
    () =>
      radioOrderOptionsList.map((item) => (
        <RadioInput
          key={item.label}
          isSelected={item.value === radioOrderOptions}
          onClick={() => setRadioOrderOptions(item.value)}
        >
          <p>{item.label}</p>

          <div className="radio">
            <div className="filled" />
          </div>
        </RadioInput>
      )),
    [radioOrderOptionsList, radioOrderOptions],
  );

  function TechnicalSheetOrderModal() {
    return (
      <Modal
        isOpen={technicalSheetOrderModal}
        setOpen={setTechnicalSheetOrderModal}
        maxWidthStyled={621}
        maxHeightStyled={428}
      >
        <ModalFilterContent>
          <ModalTitle>Ordenar</ModalTitle>

          <RadioContainer>{RadioOrderOptions}</RadioContainer>

          <ButtonBlack>Aplicar</ButtonBlack>
        </ModalFilterContent>
      </Modal>
    );
  }

  const cardsMemo = useMemo(
    () =>
      dataSheetsList.length > 0 ? (
        <CardContainer>
          {dataSheetsList?.map((dataSheet) => (
            <DataSheetCard
              key={dataSheet.id}
              dataSheet={{ id: dataSheet.id, date: dataSheet.created_at }}
            />
          ))}
        </CardContainer>
      ) : (
        <NoDataFound>
          <p>Nenhuma ficha técnica cadastrada!</p>
        </NoDataFound>
      ),
    [dataSheetsList],
  );

  const navigateToNewDataSheet = useCallback(() => {
    push(`/professional/students/${studentId}/datasheet/register`);
  }, [push, studentId]);

  return (
    <>
      <TechnicalSheetModal />
      <TechnicalSheetOrderModal />

      <NewButtonContainer>
        <ButtonWrapper>
          <NewButton onClick={navigateToNewDataSheet}>
            <FiPlus />
            <NewButtonLabel>nova ficha</NewButtonLabel>
          </NewButton>

          <FilterOptions>
            <FilterLabel>Filtrar</FilterLabel>
            <span>
              <Options>
                <MdFilterList size={30} color="#fff" />

                <div>
                  <button
                    onClick={() => setTechnicalSheetModal(true)}
                    type="button"
                  >
                    Filtrar
                  </button>

                  <div className="divider" />

                  <button
                    onClick={() => setTechnicalSheetOrderModal(true)}
                    type="button"
                  >
                    Ordenar
                  </button>
                </div>
              </Options>
            </span>
          </FilterOptions>
        </ButtonWrapper>
      </NewButtonContainer>

      <Wrapper>
        {loading ? (
          <AlertContainer>
            <MoonLoader size={30} color="#FFDF22" />
          </AlertContainer>
        ) : (
          cardsMemo
        )}
      </Wrapper>
    </>
  );
}
